import axios from "axios";
import PlanDTO from "../Models/DTOs/Plan";
import {getResolvedOffer} from "../Helpers/BillingOffers";
import {getStripeDiscount, getStripePriceId} from "../Helpers/Currency/Stripe";
import {getPromotionCode} from "../Helpers/PromotionCode";

const BACKEND_URL = process.env.REACT_APP_WEB_API_URL ?? "/";

export interface SignupQuoteRequest {
    plan_id: number;
    plan_type: number;
    price_id?: string;
    coupon_id?: string;
    offer_key?: string;
    promo?: string;
    offer_id?: number;
    offer_version?: number;
    final_amount_minor?: number;
    affiliate_code?: string;
    user_id?: string;
}

export interface SignupQuote {
    currency: string;
    amount_before_affiliate_minor: number;
    affiliate_amount_off_minor: number;
    amount_due_minor: number;
    affiliate_eligible: boolean;
}

export function signupQuoteRequest(
    plan: PlanDTO,
    userId?: string | null,
    affiliateCode?: string | null,
): SignupQuoteRequest {
    const offer = getResolvedOffer(plan);
    return {
        plan_id: Number(plan.planId),
        plan_type: plan.period === 1 ? 1 : 0,
        ...(offer ? {
            offer_key: offer.offer_key,
            offer_id: offer.id,
            offer_version: offer.version,
            final_amount_minor: offer.final_amount_minor,
            ...(offer.bucket === "targeted" && getPromotionCode()
                ? {promo: getPromotionCode()!} : {}),
        } : !plan.offerCatalogResolved ? {
            price_id: getStripePriceId(plan),
            coupon_id: getStripeDiscount(plan) ?? undefined,
        } : {}),
        // Account attribution is immutable: a URL never claims an existing account.
        ...(userId ? {user_id: userId} : affiliateCode ? {affiliate_code: affiliateCode} : {}),
    };
}

export async function GetSignupQuote(
    request: SignupQuoteRequest,
    abortController: AbortController,
    accessToken?: string | null,
): Promise<SignupQuote> {
    if (request.user_id && !accessToken) throw new Error("Please sign in again to confirm your price.");
    const {affiliate_code, ...accountRequest} = request;
    const {data} = await axios.post<SignupQuote>(
        BACKEND_URL + "v1/billing/stripe/signup-quote",
        request.user_id ? accountRequest : request,
        {
            signal: abortController.signal,
            ...(accessToken ? {headers: {Authorization: "Bearer " + accessToken}} : {}),
        },
    );
    if (!data || !/^[A-Za-z]{3}$/.test(data.currency) ||
        typeof data.affiliate_eligible !== "boolean" ||
        ![data.amount_before_affiliate_minor, data.affiliate_amount_off_minor, data.amount_due_minor]
            .every(value => Number.isSafeInteger(value) && value >= 0) ||
        data.amount_before_affiliate_minor - data.affiliate_amount_off_minor !== data.amount_due_minor) {
        throw new Error("Your price could not be confirmed. Please try again.");
    }
    return data;
}

export function signupQuotesMatch(expected: SignupQuote, actual: SignupQuote): boolean {
    return expected.currency.toLowerCase() === actual.currency.toLowerCase() &&
        expected.amount_due_minor === actual.amount_due_minor &&
        expected.amount_before_affiliate_minor === actual.amount_before_affiliate_minor &&
        expected.affiliate_amount_off_minor === actual.affiliate_amount_off_minor &&
        expected.affiliate_eligible === actual.affiliate_eligible;
}
